// @ts-strict-ignore
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { LMModelXLMultiPartFields, LMXLFileUploadEvent } from '@form/model';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { forkJoin, from, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, concatMap, map, takeUntil } from 'rxjs/operators';
import { LMFileUploadProgressComponent } from '@services/file-upload/lm-file-upload-progress.component';
import { SohoMessageService } from 'ids-enterprise-ng';
import { LMBusyIndicatorService, LMModalWrapperService, LMContextService, LMUrlUtilService } from '@app/services';
import { AppConstants, LMDialogButtonKey, LMUniqueIdService } from '@app/utils';
import { LMXLFileUploadProgressComponent } from '@services/file-upload/lm-xlfile-upload-progress.component';

export type LMXLFileUploadFIFOQueue = Array<LMXLFileUploadRequest>;

export interface LMXLFileUploadRequest {
  url: string;
  dataView: LMDataView;
  xlFieldId: string;
  uniqueID: string;
  file: File | Blob;
  chunkSize: number;
}

export interface LMXLFilePartUploadRequest {
  url: string;
  dataView: LMDataView;
  xlFieldId: string;
  uniqueID: string;
  filePart: File | Blob;
  partNumber: number;
}

/**
 * Map of all files and its upload status the service is keeping track of.
 *
 * uniqueId is the string returned by start upload process
 */
export type LMAllXLFileUploadStatus = { [ uniqueID: string ]: LMXLFileUploadStatus };

export interface LMXLFileUploadStatus {
  url: string;
  dataView: LMDataView;
  xlFieldId: string;
  uniqueID: string;
  fileName: string;
  fileSize: number;
  noOfParts: number;
  allPartsStatus: LMXLFilePartUploadStatus[];
  progressPercent: number;
  status: LMUploadStatus
}

export interface LMXLFilePartUploadStatus {
  uniqueID: string;
  partNumber: number;
  progressPercent: number;
  status: LMUploadStatus;
  error?: LMXLFileUploadError;
}

export enum LMUploadStatus {
  PENDING = 'pending',
  INPROGRESS = 'inprogress',
  COMPLETED = 'completed',
  ERROR = 'error',
  ABORTSTARTED='abortstarted',
  ABORTED = 'aborted'
}

export interface LMXLFileUploadError {
  message: string;
}

export interface LMPartProgress {
  part: number;
  progress: number;
}

@Injectable()
export class LMXLFileUploadService implements OnDestroy{
  private xlFileRequestQueue: LMXLFileUploadFIFOQueue = [];
  private allXLFileUploadStatus: LMAllXLFileUploadStatus = {};

  private activeUploads = new Map<string, Subscription>();
  private cancelSubjects = new Map<string, Subject<void>>();

  private _xlFileUploadStatusSubject$ = new Subject<boolean>();

  constructor(
    private http: HttpClient,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmModalWrapperService: LMModalWrapperService,
    private lmContextService: LMContextService,
    private sohoMessageService: SohoMessageService,
    private lmUniqueIdService: LMUniqueIdService
  ) { }

  ngOnDestroy() {
    // Cancel any active uploads
    this.activeUploads.forEach((subscription, uniqueId) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
      this.cancelSubjects.get(uniqueId)?.complete();
    });
    this.activeUploads.clear();
    this.cancelSubjects.clear();

    this._xlFileUploadStatusSubject$.complete();
  }

  public get xlFileUploadStatusSubject(): Observable<boolean> {
    return this._xlFileUploadStatusSubject$.asObservable();
  }

  public get xlfileUploadStatus(): LMAllXLFileUploadStatus {
    return this.allXLFileUploadStatus;
  }

  public uploadXLFileChunks(url: string, xlFiles: LMModelXLMultiPartFields, dataView: LMDataView, xlFieldId: string, chunkSize: number, xlFileUploadSubject$: Subject<LMXLFileUploadEvent>) {
    // Multiple fields will start uploading blobs
    // The data view contains the uniqueID from startUpload calls
    // Use the unique id to upload parts

    if (this.lmContextService.handleXLFileUploadInBackground()) {
      this.uploadXLFileChunksBackground(url, xlFiles, dataView, xlFieldId, chunkSize, xlFileUploadSubject$);
      return;
    }

    // Use the foreground method
    this.uploadXLFileChunksForeground(url, xlFiles, dataView, chunkSize, xlFileUploadSubject$);
  }

  public uploadXLFileChunksBackground(url: string, xlFiles: LMModelXLMultiPartFields, dataView: LMDataView, xlFieldId: string, chunkSize: number, xlFileUploadSubject$: Subject<LMXLFileUploadEvent>) {
    if (dataView.xlFileFields) {
      // Add the request to file upload queue.
      // Only one connection will be used to upload. Iterate and upload parts one by one.
      this.xlFileRequestQueue.push(this.createXLFileUploadRequestBG(xlFieldId, url, xlFiles, dataView, chunkSize));
      // Create the status object also for this request
      this.createXLFileUploadStatusBG(xlFieldId, url, xlFiles, dataView, chunkSize);
      // Send a message out when there are pending uploads
      this._xlFileUploadStatusSubject$.next(true);

      // Do not start another upload when one upload is in progress
      if (this.xlFileRequestQueue.length === 1 && !this.isUploadInProgressBG()) {
        setTimeout(() => {
            this.processUploadRequest(chunkSize);
        }, 5);
      }
    }
  }

  private processUploadRequest(chunkSize: number) {
    const request = this.xlFileRequestQueue.shift();
    if (request) {
      const subscription = this.uploadFile(request, chunkSize).subscribe({
        next: (response) => {
          // console.log('Part uploaded successfully:', response);
          this.allXLFileUploadStatus[ request.uniqueID ].status = LMUploadStatus.INPROGRESS;
          this.getOverallProgress(request.uniqueID);
        },
        error: (error) => {
          // console.error('Upload failed:', error);
          this.allXLFileUploadStatus[ request.uniqueID ].status = LMUploadStatus.ERROR;
          this.getOverallProgress(request.uniqueID);
          this.cleanupUpload(request.uniqueID);
          this._xlFileUploadStatusSubject$.next(true);
          // Send an abort request back to mark the header record as error so the user can retry
          this.abort(request.url, request.dataView, request.uniqueID, request.xlFieldId).subscribe();
          // Record error and proceed with the next request to upload
          // Are there more items in FIFO queue?
          if (this.xlFileRequestQueue.length > 0) {
            this.processUploadRequest(chunkSize);
          }
        },
        complete: () => {
          // console.log('All parts uploaded successfully');
          this.getOverallProgress(request.uniqueID);
          // Call Complete upload to begin assembly.
          this.completeFileUpload(request, chunkSize);
          this.cleanupUpload(request.uniqueID);
          this._xlFileUploadStatusSubject$.next(true);
          // Irrespective of whether the previous request succeeded or not, move on to the next request
          // Are there more items in FIFO queue?
          if (this.xlFileRequestQueue.length > 0) {
            this.processUploadRequest(chunkSize);
          }
        }
      });
      this.activeUploads.set(request.uniqueID, subscription);
    }
  }

  private uploadFile(request: LMXLFileUploadRequest, chunkSize: number): Observable<any> {
    this.allXLFileUploadStatus[request.uniqueID].status = LMUploadStatus.INPROGRESS;
    // Create cancel subject for this upload
    const cancelSubject = new Subject<void>();
    this.cancelSubjects.set(request.uniqueID, cancelSubject);

    const noOfParts = Math.ceil(request.file.size / chunkSize);
    const uploadRequests: LMXLFilePartUploadRequest[] = [];

    // Prepare upload requests
    for (let i = 0; i < noOfParts; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, request.file.size);
      const blob = request.file.slice(start, end);

      uploadRequests.push({
        url: request.url,
        dataView: request.dataView,
        xlFieldId: request.xlFieldId,
        uniqueID: request.uniqueID,
        filePart: blob,
        partNumber: i + 1
      });
    }

    // Convert array to observable and process sequentially
    return from(uploadRequests).pipe(
      concatMap(partRequest => this.uploadPart(partRequest, (request.file as File).name)),
      takeUntil(cancelSubject),
      // TODO: Check part failure. Is it enough if upload part throws error to terminate concatMap?
      // catchError(error => {
      //   console.log('Part upload failure. Terminating upload.');
      //   return throwError(error);
      // })
    );
  }

  private uploadPart(partRequest: LMXLFilePartUploadRequest, fileName: string): Observable<any> {
    return new Observable(subscriber => {
      try {
        const formData = new FormData();
        formData.append(partRequest.xlFieldId, partRequest.filePart);

        const extraJSONData = {
          ...JSON.parse(JSON.stringify(partRequest.dataView)),
          requestType: 'uploadXLFilePart',
          partNumber: partRequest.partNumber,
          uniqueID: partRequest.uniqueID,
          fieldId: partRequest.xlFieldId,
          fileName: fileName
        };

        formData.append('extraJSONData', JSON.stringify(extraJSONData));

        const partStatus = this.allXLFileUploadStatus[partRequest.uniqueID].allPartsStatus[partRequest.partNumber - 1];

        this.http.post(partRequest.url, formData, {
          reportProgress: true,
          observe: 'events'
        }).pipe(
          map(event => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                const progress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                // Update progress status
                partStatus.progressPercent = progress;
                partStatus.status = LMUploadStatus.INPROGRESS;
                this.getOverallProgress(partRequest.uniqueID);
                return { type: 'progress', progress: progress };

              case HttpEventType.Response:
                // Update status for successful upload
                partStatus.progressPercent = 100;
                partStatus.status = LMUploadStatus.COMPLETED;
                return { type: 'response', response: event.body };

              default:
                return { type: 'other' };
            }
          })
        ).subscribe({
          next: (response) => {
            if (response?.type !== 'response') {
              return;
            }
            const partResponse: LMDataView | LMSystemException = response.response;
            if ((partResponse as LMSystemException).status && (partResponse as LMSystemException).status == AppConstants.STATUS_SYSTEM_ERROR) {
              partStatus.status = LMUploadStatus.ERROR;
              partStatus.error = { message: (partResponse as LMSystemException).message };
              subscriber.error(partResponse as LMSystemException);
              return;
            }
            subscriber.next(response.response);
            subscriber.complete();
          },
          error: (error) => {
            // TODO: Explore retry on error https://www.learnrxjs.io/learn-rxjs/operators/error_handling/retry
            // Update status for failed upload
            partStatus.status = LMUploadStatus.ERROR;
            subscriber.error(error);
          }
        });

      } catch (error) {
        subscriber.error(error);
      }
    });
  }

  private completeFileUpload(request: LMXLFileUploadRequest, chunkSize: number) {
    // Complete is called automatically after all parts are uploaded.
    const fileStatus = this.allXLFileUploadStatus[request.uniqueID];
    const partError = fileStatus.allPartsStatus.some(part => part.status === LMUploadStatus.ERROR);
    if (!partError) {
      this.allXLFileUploadStatus[ request.uniqueID ].status = LMUploadStatus.COMPLETED;
    }
    return;
  }

  private cleanupUpload(uniqueID: string) {
    // Unsubscribe from active subscription
    const subscription = this.activeUploads.get(uniqueID);
    if (subscription) {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
      this.activeUploads.delete(uniqueID);
    }

    // Complete and remove cancel subject
    const cancelSubject = this.cancelSubjects.get(uniqueID);
    if (cancelSubject) {
      cancelSubject.complete();
      this.cancelSubjects.delete(uniqueID);
    }
  }

  public canAbortInProgressUpload(uniqueId: string): boolean {
    // Only the actor that uploaded will have the upload request in the status object
    return this.allXLFileUploadStatus.hasOwnProperty(uniqueId) &&
      (this.allXLFileUploadStatus[uniqueId].status === LMUploadStatus.INPROGRESS ||
      this.allXLFileUploadStatus[uniqueId].status === LMUploadStatus.PENDING);
  }

  public abortUpload(uniqueId: string): Observable<boolean> {
    return new Observable(observer => {
      if (this.canAbortInProgressUpload(uniqueId)) {
        // Only the actor that uploaded will have the upload request in the status object
        const uploadStatus = this.allXLFileUploadStatus[ uniqueId ];
        uploadStatus.status = LMUploadStatus.ABORTSTARTED; // This will prevent multiple requests to abort

        // Cancel concatMap requests first
        const cancelSubject = this.cancelSubjects.get(uniqueId);
        if (cancelSubject) {
          cancelSubject.next();
          cancelSubject.complete();
        }
        this.cleanupUpload(uniqueId);

        // Send an abort notification to the backend
        this.abort(uploadStatus.url, uploadStatus.dataView, uploadStatus.uniqueID, uploadStatus.xlFieldId).subscribe({
          next: (response) => {
            //console.log('Abort upload response:', response);
          },
          error: (error) => {
            observer.next(false);
            // console.error('Abort upload failed:', error);
          },
          complete: () => {
            uploadStatus.status = LMUploadStatus.ABORTED;
            observer.next(true);
          }
        });
      }
    });
  }

  private abort(url:string, dataView: LMDataView, uniqueID: string, fieldId: string):Observable<LMDataView> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    const jsonData = JSON.parse(JSON.stringify(dataView));
    jsonData.requestType = 'abortXLFileUpload';
    jsonData.fieldId = fieldId;
    jsonData.uniqueID = uniqueID;

    return this.http.post<LMPrintResponse>(url, JSON.stringify(jsonData), { headers });
  }

  private createXLFileUploadRequestBG(xlFieldId: string, url: string, xlFiles: LMModelXLMultiPartFields, dataView: LMDataView, chunkSize: number): LMXLFileUploadRequest {
    return { url, dataView, xlFieldId,
      uniqueID: dataView.xlFileFields[ xlFieldId ].uniqueID, file: xlFiles[ xlFieldId ], chunkSize } as LMXLFileUploadRequest;
  }

  private createXLFileUploadStatusBG(xlFieldId: string, url: string, xlFiles: LMModelXLMultiPartFields, dataView: LMDataView, chunkSize: number) {
    const uniqueID = dataView.xlFileFields[ xlFieldId ].uniqueID;
    const file = xlFiles[xlFieldId] as File;
    const noOfParts = Math.ceil(xlFiles[xlFieldId].size / chunkSize);
    const xlFileUploadStatus: LMXLFileUploadStatus = { url, dataView, xlFieldId, uniqueID, fileName: file.name, fileSize: Math.round(file.size / 1000000),
      noOfParts, allPartsStatus: [], progressPercent: 0, status: LMUploadStatus.PENDING };

    xlFileUploadStatus.allPartsStatus = [ ...Array(noOfParts) ].map((x, index) => ({
      uniqueID,
      status: LMUploadStatus.PENDING,
      partNumber: index + 1,
      progressPercent: 0
    }));

    this.allXLFileUploadStatus[ uniqueID ] = xlFileUploadStatus;
  }

  private getOverallProgress(uniqueID: string) {
    const fileStatus = this.allXLFileUploadStatus[ uniqueID ];
    fileStatus.progressPercent = fileStatus.allPartsStatus.reduce((totalProgress, part) => totalProgress + part.progressPercent, 0) / fileStatus.noOfParts;
    console.log('Upload Progress: ' + fileStatus.progressPercent);
  }

  public isUploadInProgressBG(): boolean {
    return Object.keys(this.allXLFileUploadStatus).some(key => this.allXLFileUploadStatus[key].status === LMUploadStatus.INPROGRESS);
  }

  public isUploadPendingBG(): boolean {
    return Object.keys(this.allXLFileUploadStatus).some(key => this.allXLFileUploadStatus[key].status === LMUploadStatus.PENDING);
  }

  public checkBeforeWindowUnload(): boolean {
    return this.isUploadPendingBG() || this.isUploadInProgressBG();
  }

  public showUploadInProgressDialog() {
    const uploadInProgress = this.lmModalWrapperService.modal(LMXLFileUploadProgressComponent);

    uploadInProgress.title(Soho.Locale.translate('UploadInProgressBanner'));
    uploadInProgress.buttons([
      {
        text: Soho.Locale.translate('Close'),
        isDefault: true,
        click: (e) => {
          uploadInProgress.close();
        }
      }
    ]);
    uploadInProgress.noRefocus(true);
    uploadInProgress.open();
  }

  // TODO: Unified menu - Before navigating to the next, call canAppPageUnload()
  public canAppPageUnload(): Observable<boolean> {
    return new Observable((observer) => {
      if (this.isUploadInProgressBG()) {
        const dialogId = 'uploadInProgress';
        let result = false;
        const buttons: SohoModalButton[] = [
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.NO ]),
            text: Soho.Locale.translate(LMDialogButtonKey.NO),
            click: (e, modal) => {
              modal.close(true);
            },
            isDefault: true
          },
          {
            id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.YES ]),
            text: Soho.Locale.translate(LMDialogButtonKey.YES),
            click: (e, modal) => {
              result = true;
              modal.close(true);
            },
          }
        ];

        const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-UploadInProgress-modal' } ];
        const messageOptions: SohoMessageOptions = {
          allowedTags: '<div>, <span>, <p>',
          message: Soho.Locale.translate('UploadInProgress'),
          buttons,
          title: Soho.Locale.translate('UploadInProgressTitle'),
          attributes
        };

        this.sohoMessageService
        .message(messageOptions)
        .open()
        .beforeClose(() => {
          observer.next(result);
          observer.complete();
          return true;
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  /**********************************************************************************************************
   * POC code to upload code in foreground
   **********************************************************************************************************/
  public uploadXLFileChunksForeground(url: string, xlFiles: LMModelXLMultiPartFields, dataView: LMDataView, chunkSize: number, xlFileUploadSubject$: Subject<LMXLFileUploadEvent>) {
    // Object.keys(xlFiles).forEach(xlFieldId => {  // TODO: If there are multiple big file inputs in a form, iterate over them
      if (dataView.xlFileFields) {
        const xlFieldId = Object.keys(xlFiles)[ 0 ];
        const xlField = dataView.xlFileFields[xlFieldId];
        const uniqueID = xlField.uniqueID;
        const totalChunks = Math.ceil(xlFiles[xlFieldId].size / chunkSize);
        const file = xlFiles[xlFieldId];
        let currentChunk = 0;

        const uploadProgress = new Subject<LMPartProgress>();
        const uploadProgress$ = uploadProgress.asObservable();
        const progressDialog = this.lmModalWrapperService.modal(LMFileUploadProgressComponent);

        progressDialog.title(Soho.Locale.translate('FileUploadProgress'));
        progressDialog.noRefocus(true);
        progressDialog.beforeOpen((dialogRef) => {
          if (dialogRef?.componentDialog) {
            dialogRef.componentDialog.xlFile = true;
            dialogRef.componentDialog.totalParts = totalChunks;
            dialogRef.componentDialog.uploadProgress$ = uploadProgress$;
          }
          return true;
        });

        const observables: Observable<any>[] = [];

        while (currentChunk < totalChunks) {
          const start = currentChunk * chunkSize;
          const end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;
          const blob = file.slice(start, end);
          currentChunk++;
          observables.push(this.uploadChunk(url, currentChunk, blob, dataView, uniqueID, xlFieldId, uploadProgress));
        }
        progressDialog.open();

        // TODO: print progress
        // TODO: unsubscribe forkJoin, especially when user cancels a chunk
        forkJoin(observables).subscribe({
          next: (results: LMDataView[]) => {
            if (this.areAllChunksUploaded(xlFieldId, results)) {
              progressDialog.componentDialog.completeUpload = true;
              this.completeUpload(url, dataView, uniqueID, xlFieldId, totalChunks, file.size).subscribe({
                next: (res) => {
                  this.lmBusyIndicatorService.showBusyIndicator();
                  progressDialog.close();
                  uploadProgress.complete();

                  if (res?.xlFileFields !== undefined && res.xlFileFields[xlFieldId].uploadStatus === 'COMPLETED') {
                    xlFileUploadSubject$.next( { dataView, uploadEventType: 'COMPLETE_UPLOAD', fieldId: xlFieldId } );
                  } else {
                    xlFileUploadSubject$.next( { dataView, uploadEventType: 'COMPLETE_UPLOAD_ERROR', fieldId: xlFieldId, errorMsg: res.xlFileFields[xlFieldId].errorMsg } );
                  }
                },
                error: (error) => {
                  // Complete upload failed. Some failure in the backend
                  // that will require all parts to be uploaded again.
                  // Retry logic will not work in this case.
                  this.lmBusyIndicatorService.showBusyIndicator();
                  progressDialog.close();
                  uploadProgress.complete();

                  xlFileUploadSubject$.next( { dataView, uploadEventType: 'COMPLETE_UPLOAD_ERROR', fieldId: xlFieldId } );
                }
              });
            } else {
              // TODO: Not all parts are uploaded. Enhance progress dialog to provide retry logic
              // TODO: Until then, just close and complete for cleanup.
              this.lmBusyIndicatorService.showBusyIndicator();
              progressDialog.close();
              uploadProgress.complete();
              // TODO: Send message out to Progress component to indicate failed parts
              xlFileUploadSubject$.next( { dataView, uploadEventType: 'PART_UPLOAD_ERROR', fieldId: xlFieldId } );
            }
          },
          error: (error) => {
            this.lmBusyIndicatorService.showBusyIndicator();
            progressDialog.close();
            uploadProgress.complete();
          }
        });
      }
    // }); // TODO: If there are multiple big file inputs in a form, iterate over them
  }

  private areAllChunksUploaded(fieldId: string, results: LMDataView[]): boolean {
    let allUploaded = false;
    if (results?.length) {
      allUploaded = !(results.some(dv => dv.xlFileFields === undefined || dv.xlFileFields[fieldId].uploadStatus === 'ERROR'));
    }
    return allUploaded;
  }

  private getUploadStatusOfParts(fieldId: string, results: LMDataView[]) {
    // An array of part number and status
  }

  private uploadChunk(url:string, partNumber:number, blob: Blob, dataView: LMDataView, uniqueID: string, fieldId: string, uploadProgress: Subject<LMPartProgress>): Observable<LMDataView> {
    const formData = new FormData();
    formData.append(fieldId, blob);

    const extraJSONData = JSON.parse(JSON.stringify(dataView));
    extraJSONData.requestType = 'uploadXLFilePart';
    extraJSONData.partNumber = partNumber;
    extraJSONData.uniqueID = uniqueID;
    extraJSONData.fieldId = fieldId;
    formData.append('extraJSONData', JSON.stringify(extraJSONData));

    // TODO: retry on error
    const req = new HttpRequest('POST', url, formData, { reportProgress: true });

    // Return an observable that emits progress events
    return this.http.request(req).pipe(
      // filter((event: HttpEvent<LMDataView>) => [ HttpEventType.UploadProgress, HttpEventType.Response ].includes(event.type)),
      map((event: HttpEvent<LMDataView>) => {
        if (event) {
          if (event.type === HttpEventType.UploadProgress) {
            // Calculate percentage
            const percentDone = Math.round(100 * event.loaded / event.total);
            uploadProgress.next({ part: partNumber, progress: percentDone });
          } else if (event.type === HttpEventType.Response) {
            // Return the response when upload is complete
            return event.body;
          }
        }
      }),
      catchError(error => throwError(error)),
    );
  }

  private completeUpload(url:string, dataView: LMDataView, uniqueID: string, fieldId: string, totalParts: number, crcFileSize: number):Observable<LMDataView> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    const jsonData = JSON.parse(JSON.stringify(dataView));
    jsonData.requestType = 'completeXLFileUpload';
    jsonData.fieldId = fieldId;
    jsonData.totalParts = totalParts;
    jsonData.uniqueID = uniqueID;
    // jsonData.crcFileSize = crcFileSize;

    return this.http.post<LMPrintResponse>(url, JSON.stringify(jsonData), { headers });
  }
}
